import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Upload, Video, FileText, BarChart3, FileSearch, Youtube,
  Trash2, FolderOpen, HardDrive, Library, AlertCircle,
} from 'lucide-react';
import {
  Tabs, TabsList, TabsTrigger, TabsContent, Pagination, Spinner,
} from '../../components/design-system';
import { myLibraryService } from '../../services/myLibraryService';
import { getUser, formatFileSize } from '../../utils/helpers';

const ITEM_TYPE_CONFIG = {
  video:    { icon: Video,      color: 'text-blue-400',    bgColor: 'bg-blue-500/10' },
  document: { icon: FileText,   color: 'text-emerald-400', bgColor: 'bg-emerald-500/10' },
  analysis: { icon: BarChart3,  color: 'text-purple-400',  bgColor: 'bg-purple-500/10' },
  report:   { icon: FileSearch, color: 'text-orange-400',  bgColor: 'bg-orange-500/10' },
  youtube:  { icon: Youtube,    color: 'text-red-400',     bgColor: 'bg-red-500/10' },
};

const STATUS_STYLES = {
  PROCESSING: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  READY:      'bg-green-500/15 text-green-400 border-green-500/30',
  FAILED:     'bg-red-500/15 text-red-400 border-red-500/30',
};

const TAB_FILTERS = [
  { value: 'all',       label: 'All' },
  { value: 'video',     label: 'Videos' },
  { value: 'document',  label: 'Documents' },
  { value: 'analysis',  label: 'Analysis' },
  { value: 'report',    label: 'Reports' },
  { value: 'youtube',   label: 'YouTube' },
];

const PAGE_SIZE = 20;

function relativeTime(dateString) {
  if (!dateString) return '';
  const diffSec = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  const diffMon = Math.floor(diffDay / 30);
  if (diffMon < 12) return `${diffMon}mo ago`;
  return `${Math.floor(diffMon / 12)}y ago`;
}

/** Single library item card with type icon, status badge, and delete action. */
function LibraryItemCard({ item, onDelete }) {
  const [confirming, setConfirming] = useState(false);
  const cfg = ITEM_TYPE_CONFIG[item.item_type] || ITEM_TYPE_CONFIG.document;
  const Icon = cfg.icon;
  const statusStyle = STATUS_STYLES[item.status] || STATUS_STYLES.READY;

  const handleDelete = (e) => {
    e.stopPropagation();
    if (confirming) {
      onDelete(item.id);
      setConfirming(false);
    } else {
      setConfirming(true);
      setTimeout(() => setConfirming(false), 3000);
    }
  };

  return (
    <div className="group bg-[#111111] border border-[#1A1A1A] rounded-lg p-4 hover:border-[#E8B800]/30 transition-colors flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg ${cfg.bgColor} flex items-center justify-center`}>
          <Icon size={20} className={cfg.color} />
        </div>
        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${statusStyle}`}>
          {item.status || 'READY'}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-medium text-[#F5F5F5] truncate">{item.title || 'Untitled'}</h3>
        {item.description && (
          <p className="mt-1 text-xs text-[#777777] line-clamp-2">{item.description}</p>
        )}
      </div>
      <div className="flex items-center justify-between text-[11px] text-[#666666]">
        <span>{item.file_size ? formatFileSize(item.file_size) : '--'}</span>
        <span>{relativeTime(item.created_at)}</span>
      </div>
      <button
        onClick={handleDelete}
        className={`self-end flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${
          confirming
            ? 'bg-red-500/20 text-red-400'
            : 'text-[#555555] hover:text-red-400 opacity-0 group-hover:opacity-100'
        }`}
      >
        <Trash2 size={12} />
        {confirming ? 'Confirm?' : 'Delete'}
      </button>
    </div>
  );
}

/** Horizontal storage usage bar with gold fill. */
function StorageBar({ used, total }) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-3 text-xs text-[#999999]">
      <HardDrive size={14} className="text-[#666666] flex-shrink-0" />
      <div className="flex-1 h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
        <div
          className="h-full bg-[#E8B800] rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="flex-shrink-0">
        {formatFileSize(used)} / {formatFileSize(total)}
      </span>
    </div>
  );
}

/** Shared content renderer used inside every TabsContent panel. */
function LibraryContent({ loading, error, items, search, onDelete }) {
  if (error) {
    return (
      <div className="flex items-center gap-2 mb-4 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
        <AlertCircle size={14} />
        {error}
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" tip="Loading library..." />
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <FolderOpen size={48} className="text-[#333333] mb-4" />
        <h3 className="text-base font-medium text-[#999999]">
          {search ? 'No results found' : 'Your library is empty'}
        </h3>
        <p className="mt-1 text-sm text-[#666666]">
          {search ? 'Try a different search term.' : 'Upload videos, documents, or analysis files to get started.'}
        </p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {items.map((item) => (
        <LibraryItemCard key={item.id} item={item} onDelete={onDelete} />
      ))}
    </div>
  );
}

/**
 * MyLibraryPage -- Browse, search, and manage user-uploaded content.
 *
 * Features:
 *  - Tabbed filtering by content type (All, Videos, Documents, Analysis, Reports, YouTube)
 *  - Full-text search across titles and descriptions
 *  - Storage usage indicator
 *  - Paginated grid of LibraryItemCards with delete confirmation
 */
export default function MyLibraryPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [storage, setStorage] = useState({ used: 0, total: 0 });

  const userId = getUser()?.id;

  const fetchItems = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const data = await myLibraryService.getItems(userId, {
        type: activeTab === 'all' ? undefined : activeTab,
        search: search || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch library items:', err);
      setError('Unable to load library. Please try again.');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [userId, activeTab, search, page]);

  const fetchStorage = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await myLibraryService.getStorageUsage(userId);
      setStorage({ used: data.used_bytes || 0, total: data.total_bytes || 5 * 1024 * 1024 * 1024 });
    } catch { /* non-critical */ }
  }, [userId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchStorage(); }, [fetchStorage]);
  useEffect(() => { setPage(1); }, [activeTab, search]);

  const handleDelete = async (id) => {
    try {
      await myLibraryService.deleteItem(id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      setTotal((prev) => Math.max(0, prev - 1));
      fetchStorage();
    } catch (err) {
      console.error('Delete failed:', err);
      setError('Failed to delete item.');
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-black text-[#F5F5F5]">
      {/* Header */}
      <header className="flex-shrink-0 border-b px-6 py-3 bg-[#0A0A0A] border-[#1A1A1A]">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Library size={20} className="text-[#E8B800]" />
            <h1 className="text-lg font-semibold">My Library</h1>
          </div>
          <div className="flex items-center gap-3 flex-1 max-w-md">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666666]" />
              <input
                type="text"
                placeholder="Search library..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-sm bg-[#111111] border border-[#1A1A1A] rounded-lg text-[#F5F5F5] placeholder-[#555555] focus:outline-none focus:border-[#E8B800]/50 transition-colors"
              />
            </div>
          </div>
          <button className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-[#E8B800] text-black rounded-lg hover:bg-[#E8B800]/90 transition-colors">
            <Upload size={14} />
            Upload
          </button>
        </div>
        <div className="mt-2">
          <StorageBar used={storage.used} total={storage.total} />
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#0A0A0A] border border-[#1A1A1A] mb-4">
            {TAB_FILTERS.map((t) => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className="data-[state=active]:bg-[#1A1A1A] data-[state=active]:text-[#E8B800]"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {TAB_FILTERS.map((t) => (
            <TabsContent key={t.value} value={t.value}>
              <LibraryContent loading={loading} error={error} items={items} search={search} onDelete={handleDelete} />
            </TabsContent>
          ))}
        </Tabs>
        {!loading && totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <Pagination current={page} total={total} pageSize={PAGE_SIZE} onChange={(p) => setPage(p)} />
          </div>
        )}
      </div>
    </div>
  );
}
